/**
 * Repositório local: lê e grava no Storage e põe cada alteração na fila de envio.
 *
 * Toda leitura e escrita é local; a interface responde na hora (plano, seção 19.2).
 * Quem chama nunca escreve direto no Storage.
 */
import { uuid } from '../domain/ids';
import { toIso } from '../domain/time';
import {
  type EntityMap,
  type EntityType,
  PROFESSIONAL_READONLY_FIELDS,
  SERVER_MANAGED_FIELDS,
  entitySchemas,
} from '../schemas/entities';
import type { OutboxEntry, Storage } from '../storage/storage';

export type ChangeListener = (entities: Set<EntityType>) => void;

/** Campos que o aparelho nunca envia na fila, por entidade. */
const NOT_SENT: Record<EntityType, ReadonlySet<string>> = (() => {
  const base = [...SERVER_MANAGED_FIELDS, 'id', 'deleted_at', 'client_updated_at'];
  const out = {} as Record<EntityType, ReadonlySet<string>>;
  for (const e of Object.keys(entitySchemas) as EntityType[]) out[e] = new Set(base);
  out.professional = new Set([...base, ...PROFESSIONAL_READONLY_FIELDS]);
  out.client = new Set([...base, 'access_code']);
  out.appointment = new Set([...base, 'conflict']);
  return out;
})();

export type CreateInput<E extends EntityType> = Partial<EntityMap[E]> & Record<string, unknown>;

export interface LocalDbOptions {
  /** id da profissional dona dos dados */
  getProfessionalId: () => string | null;
  now?: () => number;
  /** registro do servidor fora do formato esperado (para diagnóstico) */
  onInvalidRecord?: (entity: EntityType, record: unknown, issues: unknown) => void;
}

export class LocalDb {
  private listeners = new Set<ChangeListener>();
  private batchDepth = 0;
  private pendingEmit = new Set<EntityType>();
  private unsubscribeExternal: (() => void) | null = null;

  constructor(
    readonly storage: Storage,
    private readonly opts: LocalDbOptions,
  ) {
    this.unsubscribeExternal =
      storage.onExternalChange?.((entities) => this.emitLocal(new Set(entities))) ?? null;
  }

  dispose(): void {
    this.unsubscribeExternal?.();
    this.listeners.clear();
  }

  now(): number {
    return this.opts.now ? this.opts.now() : Date.now();
  }

  nowIso(): string {
    return toIso(this.now());
  }

  professionalId(): string {
    const id = this.opts.getProfessionalId();
    if (!id) throw new Error('Nenhuma profissional ativa no aparelho.');
    return id;
  }

  // ------------------------------------------------------------- eventos

  subscribe(listener: ChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emitLocal(entities: Set<EntityType>): void {
    for (const l of this.listeners) l(entities);
  }

  private touched(entity: EntityType): void {
    this.pendingEmit.add(entity);
    if (this.batchDepth === 0) this.flushEmit();
  }

  private flushEmit(): void {
    if (this.pendingEmit.size === 0) return;
    const set = new Set(this.pendingEmit);
    this.pendingEmit.clear();
    this.emitLocal(set);
    this.storage.notifyExternal?.([...set]);
  }

  /** Agrupa várias gravações numa transação e num só aviso de mudança. */
  async batch<T>(fn: () => Promise<T>): Promise<T> {
    this.batchDepth++;
    try {
      // Sempre uma função async: o Dexie só acompanha a transação por funções async nativas
      // (com uma função comum que devolve promessa, a transação fecha antes da hora).
      return await this.storage.transaction(async () => fn());
    } finally {
      this.batchDepth--;
      if (this.batchDepth === 0) this.flushEmit();
    }
  }

  // ------------------------------------------------------------- leitura

  /** Registros ativos (sem os excluídos). */
  async all<E extends EntityType>(entity: E): Promise<EntityMap[E][]> {
    const rows = await this.storage.getAll(entity);
    return rows.filter((r) => !r.deleted_at);
  }

  async range<E extends EntityType>(
    entity: E,
    field: 'start_utc' | 'client_id' | 'date',
    lower: string,
    upper: string,
  ): Promise<EntityMap[E][]> {
    const rows = await this.storage.getRange(entity, field, lower, upper);
    return rows.filter((r) => !r.deleted_at);
  }

  async get<E extends EntityType>(entity: E, id: string): Promise<EntityMap[E] | undefined> {
    const r = await this.storage.get(entity, id);
    return r && !r.deleted_at ? r : undefined;
  }

  /** Inclui excluídos. */
  async getRaw<E extends EntityType>(entity: E, id: string): Promise<EntityMap[E] | undefined> {
    return this.storage.get(entity, id);
  }

  // ------------------------------------------------------------- escrita

  async create<E extends EntityType>(entity: E, input: CreateInput<E>): Promise<EntityMap[E]> {
    const now = this.nowIso();
    const raw: Record<string, unknown> = {
      ...input,
      id: input.id ?? uuid(),
      created_at: input.created_at ?? null,
      updated_at: null,
      client_updated_at: now,
      version: 0,
      deleted_at: null,
    };
    if (entity !== 'professional') raw.professional_id = this.professionalId();
    const record = entitySchemas[entity].parse(raw) as EntityMap[E];
    await this.batch(async () => {
      await this.storage.put(entity, record);
      await this.enqueue(entity, record.id, 'upsert', now, 0, this.sendable(entity, record as Record<string, unknown>));
    });
    this.touched(entity);
    return record;
  }

  async update<E extends EntityType>(entity: E, id: string, patch: Partial<EntityMap[E]>): Promise<EntityMap[E]> {
    const existing = await this.storage.get(entity, id);
    if (!existing) throw new Error(`Registro não encontrado: ${entity} ${id}`);
    if (existing.deleted_at) return existing;
    const now = this.nowIso();
    const merged = { ...existing, ...patch, client_updated_at: now } as Record<string, unknown>;
    const record = entitySchemas[entity].parse(merged) as EntityMap[E];
    const changed: Record<string, unknown> = {};
    let changedLocal = false;
    const notSent = NOT_SENT[entity];
    for (const key of Object.keys(patch)) {
      const before = (existing as Record<string, unknown>)[key];
      const after = (record as Record<string, unknown>)[key];
      if (JSON.stringify(before) === JSON.stringify(after)) continue;
      changedLocal = true;
      if (!notSent.has(key)) changed[key] = after;
    }
    // Campo especial de estorno de conclusão (não é coluna)
    if ((patch as Record<string, unknown>)._reverse === true) changed._reverse = true;
    if (!changedLocal && Object.keys(changed).length === 0) return existing;
    await this.batch(async () => {
      await this.storage.put(entity, record);
      // Campos do servidor mudam só no aparelho (o servidor sobrescreve depois)
      if (Object.keys(changed).length > 0) await this.enqueue(entity, id, 'upsert', now, existing.version, changed);
    });
    this.touched(entity);
    return record;
  }

  async remove(entity: EntityType, id: string): Promise<void> {
    const existing = await this.storage.get(entity, id);
    if (!existing || existing.deleted_at) return;
    const now = this.nowIso();
    const record = { ...existing, deleted_at: now, client_updated_at: now };
    await this.batch(async () => {
      await this.storage.put(entity, record as never);
      await this.enqueue(entity, id, 'delete', now, existing.version, {});
    });
    this.touched(entity);
  }

  /**
   * Grava um registro vindo do servidor. Se ainda houver alterações locais
   * pendentes para ele, elas são reaplicadas por cima (o servidor decide no próximo envio).
   */
  async applyRemote<E extends EntityType>(entity: E, remote: unknown): Promise<EntityMap[E] | null> {
    const parsed = entitySchemas[entity].safeParse(remote);
    if (!parsed.success) {
      this.opts.onInvalidRecord?.(entity, remote, parsed.error.issues);
      return null;
    }
    let record = parsed.data as EntityMap[E];
    const pending = await this.storage.outboxFor(entity, record.id);
    if (pending.length > 0) {
      let merged = { ...record } as Record<string, unknown>;
      for (const p of pending) {
        if (p.op === 'delete') merged = { ...merged, deleted_at: merged.deleted_at ?? p.client_updated_at };
        else {
          const { _reverse: _ignored, ...data } = p.data;
          merged = { ...merged, ...data, client_updated_at: p.client_updated_at };
        }
      }
      const reparsed = entitySchemas[entity].safeParse(merged);
      if (reparsed.success) record = reparsed.data as EntityMap[E];
    }
    await this.storage.put(entity, record);
    this.touched(entity);
    return record;
  }

  /** Grava sem fila (dados de exemplo, perfil vindo do login). */
  async putLocal<E extends EntityType>(entity: E, input: CreateInput<E>): Promise<EntityMap[E]> {
    const raw: Record<string, unknown> = { ...input, id: input.id ?? uuid() };
    if (entity !== 'professional' && !raw.professional_id) raw.professional_id = this.professionalId();
    const record = entitySchemas[entity].parse(raw) as EntityMap[E];
    await this.storage.put(entity, record);
    this.touched(entity);
    return record;
  }

  private sendable(entity: EntityType, record: Record<string, unknown>): Record<string, unknown> {
    const notSent = NOT_SENT[entity];
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(record)) if (!notSent.has(k)) out[k] = v;
    return out;
  }

  private async enqueue(
    entity: EntityType,
    id: string,
    op: OutboxEntry['op'],
    clientUpdatedAt: string,
    baseVersion: number,
    data: Record<string, unknown>,
  ): Promise<void> {
    await this.storage.enqueue({
      op_id: uuid(),
      entity,
      id,
      op,
      client_updated_at: clientUpdatedAt,
      base_version: baseVersion,
      data,
    });
  }
}
